/* What they are studying, where, when and how.

   These fields were scattered on the student page -- the program name and its
   add-ons sat inside Personal Information, while campus, delivery method and
   the pathway option sat inside Enrollment -- so the two pages disagreed about
   what counted as program information. This is the one Program card both pages
   render, matching how the application form asks the questions.

   Values are codes, not display strings (see `lib/personView`), so the same
   option lists drive both pages. */

import { GraduationCap } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EditableField } from '@/components/admin/ProfileField';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import {
  ATTENDANCE_OPTIONS,
  CAMPUS_OPTIONS,
  PATHWAY_OPTIONS,
  labelFromOptions,
} from '@/lib/applications';
import {
  SelectControl,
  inputClass,
  sectionCardClass,
  sectionTitleClass,
  sectionTitleStyle,
} from './controls';

function safeList(value) {
  return Array.isArray(value) ? value : [];
}

export default function ProgramSection({
  person,
  form,
  editing = false,
  setField,
  onChangeProgram,
  programOptions = [],
  startingDateOptions = [],
  availableAddOns = [],
  enrollmentStatus = '',
  enrollmentStatusDraft,
  enrollmentStatusOptions = [],
  onEnrollmentStatusChange,
  enrollmentStatusFallback = 'Not set',
  // The two pages label the intake differently because they mean different
  // things by it: an applicant states an intended start, a student has one.
  startLabel = 'Intended Starting Date',
  formatStart = (value) => value || '',
  endDate = '',
  englishProficiencyTypes = [],
  showEnglishProficiency = true,
  showApplicationFee = true,
  title = 'Program',
}) {
  const view = person || {};
  const draft = form || view;
  const set = (key) => (value) => setField?.(key, value);
  const setText = (key) => (event) => setField?.(key, event.target.value);

  const selected = safeList(draft.selectedAddOns);
  const toggleAddOn = (id, checked) => {
    const next = checked
      ? [...selected.filter((item) => item !== id), id]
      : selected.filter((item) => item !== id);
    setField?.('selectedAddOns', next);
  };

  const addOnLabels = safeList(view.selectedAddOns)
    .map((id) => availableAddOns.find((addOn) => addOn.id === id)?.label || id)
    .join(', ');

  return (
    <Card className={sectionCardClass}>
      <CardHeader className="pb-3">
        <CardTitle className={sectionTitleClass} style={sectionTitleStyle}>
          <GraduationCap className="w-4 h-4 text-[#D4AF37]" /> {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <EditableField label="Program" editing={editing} display={view.program} full>
          <SelectControl
            value={draft.program}
            onChange={onChangeProgram || set('program')}
            options={programOptions}
          />
        </EditableField>
        <EditableField
          label="Enrollment status"
          editing={editing && Boolean(onEnrollmentStatusChange)}
          display={enrollmentStatus || enrollmentStatusFallback}
        >
          <SelectControl
            value={enrollmentStatusDraft ?? enrollmentStatus}
            options={enrollmentStatusOptions}
            onChange={onEnrollmentStatusChange}
            placeholder="Select enrollment status"
            ariaLabel="Enrollment status"
          />
        </EditableField>
        <EditableField label="Program add-ons" editing={editing} display={addOnLabels} full>
          <div className="space-y-2 rounded-lg border border-[rgba(6,43,26,0.12)] p-3">
            {availableAddOns.length ? availableAddOns.map((addOn) => (
              <label key={addOn.id} className="flex items-start gap-3 text-sm text-[#062B1A]">
                <Switch
                  checked={selected.includes(addOn.id)}
                  onCheckedChange={(checked) => toggleAddOn(addOn.id, checked)}
                />
                <span>
                  <span className="font-medium">{addOn.label}</span>
                  {addOn.description ? (
                    <span className="block text-xs text-[#062B1A]/55">{addOn.description}</span>
                  ) : null}
                </span>
              </label>
            )) : <p className="text-sm text-[#062B1A]/55">This program has no optional add-ons.</p>}
          </div>
        </EditableField>
        <EditableField label={startLabel} editing={editing} display={formatStart(view.intendedStart)}>
          <SelectControl
            value={draft.intendedStart}
            onChange={set('intendedStart')}
            options={startingDateOptions}
          />
        </EditableField>
        {/* Derived from the program and the add-ons chosen, so it is shown but
            never typed. */}
        <EditableField
          label="Ending Date"
          editing={false}
          display={endDate ? formatStart(endDate) : 'Not set'}
        />
        <EditableField label="Campus" editing={editing} display={labelFromOptions(CAMPUS_OPTIONS, view.campus)}>
          <SelectControl value={draft.campus} onChange={set('campus')} options={CAMPUS_OPTIONS} />
        </EditableField>
        <EditableField
          label="Delivery Method"
          editing={editing}
          display={labelFromOptions(ATTENDANCE_OPTIONS, view.attendanceMode)}
        >
          <SelectControl value={draft.attendanceMode} onChange={set('attendanceMode')} options={ATTENDANCE_OPTIONS} />
        </EditableField>
        <EditableField
          label="University pathway"
          editing={editing}
          display={labelFromOptions(PATHWAY_OPTIONS, view.universityPathway)}
        >
          <SelectControl value={draft.universityPathway} onChange={set('universityPathway')} options={PATHWAY_OPTIONS} />
        </EditableField>
        {showEnglishProficiency && (
          <>
            <EditableField
              label="English Proficiency"
              editing={editing}
              display={view.englishProficiencyType}
            >
              <SelectControl
                value={draft.englishProficiencyType}
                onChange={set('englishProficiencyType')}
                options={englishProficiencyTypes}
              />
            </EditableField>
            <EditableField label="Proficiency Score" editing={editing} display={view.englishProficiencyScore}>
              <Input
                value={draft.englishProficiencyScore || ''}
                onChange={setText('englishProficiencyScore')}
                className={inputClass}
              />
            </EditableField>
          </>
        )}
        {showApplicationFee && (
          <EditableField
            label="Application Fee"
            editing={editing}
            display={view.applicationFeePaid ? 'Paid' : 'Not paid'}
          >
            <div className="flex items-center gap-3">
              <Switch
                checked={Boolean(draft.applicationFeePaid)}
                onCheckedChange={(checked) => setField?.('applicationFeePaid', checked)}
              />
              <span className="text-sm text-[#062B1A]">
                {draft.applicationFeePaid ? 'Paid' : 'Not paid'}
              </span>
            </div>
          </EditableField>
        )}
      </CardContent>
    </Card>
  );
}
